import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, setSessionUser } from "./api";
import {
  IconDashboard,
  IconList,
  IconCalendar,
  IconClock,
  IconRefresh,
  IconShield,
  IconDatabase,
  IconActivity,
  IconSun,
  IconMoon,
  IconLogout,
  IconHome,
} from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import Login from "./components/Login";
import AccessGate from "./components/AccessGate";
import AvisoSistema from "./components/AvisoSistema";
import PainelView from "./components/PainelView";
import SolicitacoesView from "./components/SolicitacoesView";
import AgendaView from "./components/AgendaView";
import LembretesView from "./components/LembretesView";
import AtualizacoesView from "./components/AtualizacoesView";
import AuditoriaView from "./components/AuditoriaView";
import BackupView from "./components/BackupView";
import MonitorView from "./components/MonitorView";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import { iniciarPwa, sincronizarNotificacao } from "./pwa";
import { readSsoUser, clearSsoFromUrl, goToSystem, SYSTEMS, checkAcesso, isAdminSupremo } from "./integration";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  type Visualizacao,
} from "./modo-leitura";

/**
 * DESENVOLVIMENTO DE SOFTWARE — o app.
 *
 * Oito abas, na ordem do dia de trabalho: o que preciso fazer (Painel), o que
 * me pediram (Solicitações), o que está marcado (Agenda), o que não posso
 * esquecer (Lembretes), o que eu entreguei (Atualizações), o que está
 * acontecendo nos sistemas (Auditoria), os sistemas ao vivo — logs, saúde e
 * máquina (Monitor) — e a cópia de segurança de tudo (Backup).
 */

type GateStatus = "checking" | "allowed" | "denied" | "error";

const SESSION_KEY = "sercamp-desenvolvedor-user";

const ABAS = [
  { key: "painel", label: "Painel", Icon: IconDashboard, sub: "O que precisa da sua atenção hoje" },
  { key: "solicitacoes", label: "Solicitações", Icon: IconList, sub: "Pedidos de adaptação e configuração dos sistemas" },
  { key: "agenda", label: "Agenda", Icon: IconCalendar, sub: "A semana, os pedidos de reunião e os horários livres" },
  { key: "lembretes", label: "Lembretes", Icon: IconClock, sub: "O que não pode ser esquecido" },
  { key: "atualizacoes", label: "Atualizações", Icon: IconRefresh, sub: "O que foi entregue e o aviso dentro dos sistemas" },
  { key: "auditoria", label: "Auditoria", Icon: IconShield, sub: "Tudo o que acontece nos sistemas da SERCAMP" },
  { key: "monitor", label: "Monitor", Icon: IconActivity, sub: "Os sistemas ao vivo: logs, saúde e a máquina" },
  { key: "backup", label: "Backup", Icon: IconDatabase, sub: "A cópia dos bancos, dos documentos e da configuração" },
] as const;

/** Login inicial: SSO do Portal (URL) na frente; senão a sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave). */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && ABAS.some((t) => t.key === k) ? k : "painel";
  } catch {
    return "painel";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [gate, setGate] = useState<GateStatus>(user ? "checking" : "allowed");
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);
  /* Recarrega o painel quando algo muda em outra aba (uma solicitação nova, um
     lembrete concluído): o número da barra tem de acompanhar. */
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  /* PEDIDOS DE REUNIÃO ESPERANDO RESPOSTA — o número ao lado de "Agenda" e o
     aviso na tela quando chega um novo com o app aberto (com o app fechado,
     quem avisa é a notificação do celular — ver src/pwa.ts). */
  const [pendentes, setPendentes] = useState(0);
  const pendentesAntes = useRef<number | null>(null);
  /* SOLICITAÇÕES NOVAS (status "Recebida") — o número ao lado de
     "Solicitações" e o aviso quando chega uma de outro sistema com o app
     aberto. Guarda os ids já vistos: comparar só a quantidade esconderia uma
     que chegou no mesmo minuto em que outra foi analisada. */
  const [novas, setNovas] = useState(0);
  const novasAntes = useRef<Set<number> | null>(null);
  /* O aviso na tela (pedido de reunião ou solicitação nova) e a aba que o
     botão dele abre. */
  const [aviso, setAviso] = useState<{ texto: string; aba: string; botao: string } | null>(null);
  /* Remonta a lista de solicitações quando se chega nela por um aviso — a
     tela não se recarrega sozinha, e a solicitação nova tem de aparecer. */
  const [chaveSolic, setChaveSolic] = useState(0);
  /* No celular o menu é uma faixa que rola de lado (ver styles.css). A aba
     aberta pode estar fora da vista — ao chegar por um link direto (#tab=),
     a pessoa via a faixa começando no "Painel" sem enxergar onde está. */
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);

  /* Traz a aba aberta para o meio da faixa (só tem efeito onde ela rola).
     `gate` entra nas dependências porque, enquanto o portão de acesso não
     libera, o menu nem existe na tela: sem isso o efeito rodava cedo demais,
     não achava nada, e quem chegasse por um link direto continuava vendo a
     faixa começando no "Painel". */
  useEffect(() => {
    if (gate !== "allowed") return;
    // `block: "nearest"` é o que impede a página inteira de pular junto.
    // Instantâneo, e não "smooth": ao abrir a tela a faixa já tem de estar no
    // lugar certo — a animação deixava a barra deslizando sozinha na cara de
    // quem acabou de entrar, e no celular parece defeito.
    const rolar = () =>
      navRef.current
        ?.querySelector(".nav-item.active")
        ?.scrollIntoView({ inline: "center", block: "nearest" });
    rolar();
    /* E de novo quando as fontes terminam de carregar: a largura das abas muda
       com a fonte final, e a primeira conta parava a faixa no meio do caminho —
       a aba aberta ficava aparecendo pela metade, colada na borda. */
    document.fonts?.ready.then(rolar).catch(() => {});
  }, [active, gate]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);

  // Portão de acesso: confirma com o Portal antes de montar o app.
  useEffect(() => {
    if (!user) {
      setGate("allowed");
      return;
    }
    setSessionUser(user);
    if (isAdminSupremo(user)) {
      setGate("allowed");
      return;
    }
    let cancelado = false;
    setGate("checking");
    checkAcesso(user)
      .then((r) => {
        if (cancelado) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        setGate(r.allowed ? "allowed" : "denied");
      })
      .catch(() => {
        if (!cancelado) setGate("error");
      });
    return () => {
      cancelado = true;
    };
  }, [user]);

  /* Clique na notificação com o app já aberto: o service worker manda o
     endereço do aviso (#tab=agenda, #tab=solicitacoes…) e a aba certa abre. */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const ouvir = (e: MessageEvent) => {
      if (e.data?.tipo === "abrir") {
        const m = String(e.data.url ?? "").match(/[#&]tab=([^&]+)/);
        const k = m ? decodeURIComponent(m[1]) : "";
        const destino = ABAS.some((t) => t.key === k) ? k : "agenda";
        setActive(destino);
        if (destino === "solicitacoes") setChaveSolic((v) => v + 1);
        recarregar();
      }
    };
    navigator.serviceWorker.addEventListener("message", ouvir);
    return () => navigator.serviceWorker.removeEventListener("message", ouvir);
  }, [recarregar]);

  // Contagem dos pedidos e das solicitações: a cada minuto, e sempre que algo muda numa aba.
  useEffect(() => {
    if (!user || gate !== "allowed") return;
    iniciarPwa();
    // Confere (e conserta, se preciso) a inscrição deste aparelho no servidor:
    // é o que impede o aviso de parar de chegar sem ninguém perceber.
    void sincronizarNotificacao();
    let vivo = true;
    const contarReunioes = async () => {
      try {
        const lista = (await api.reunioes.listar()) as { status: string; solicitante?: string }[];
        if (!vivo) return;
        const aguardando = lista.filter((r) => r.status === "Solicitada");
        const antes = pendentesAntes.current;
        if (antes !== null && aguardando.length > antes) {
          const quem = aguardando[aguardando.length - 1]?.solicitante;
          setAviso({
            texto:
              aguardando.length - antes === 1 && quem
                ? quem + " pediu uma reunião."
                : aguardando.length - antes + " pedidos de reunião novos.",
            aba: "agenda",
            botao: "Ver pedido",
          });
        }
        pendentesAntes.current = aguardando.length;
        setPendentes(aguardando.length);
      } catch {
        /* sem rede agora — tenta de novo no próximo minuto */
      }
    };
    const contarSolicitacoes = async () => {
      try {
        const lista = (await api.solicitacoes.listar({ status: "Recebida" })) as {
          id: number;
          origem: string;
          solicitante?: string | null;
          sistema?: string | null;
        }[];
        if (!vivo) return;
        const vistas = novasAntes.current;
        const chegaram = vistas ? lista.filter((s) => !vistas.has(s.id) && s.origem === "Sistema") : [];
        if (chegaram.length > 0) {
          const s = chegaram[0];
          setAviso({
            texto:
              chegaram.length === 1
                ? "Solicitação nova" + (s.solicitante ? " de " + s.solicitante : "") + "."
                : chegaram.length + " solicitações novas chegaram.",
            aba: "solicitacoes",
            botao: "Ver solicitação",
          });
        }
        novasAntes.current = new Set(lista.map((s) => s.id));
        setNovas(lista.length);
      } catch {
        /* sem rede agora — tenta de novo no próximo minuto */
      }
    };
    const contar = () => {
      contarReunioes();
      contarSolicitacoes();
    };
    contar();
    const id = setInterval(contar, 60_000);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [user, gate, versao]);

  // Nome de exibição (o /api/me também confirma o acesso do lado do servidor).
  useEffect(() => {
    if (!user || gate !== "allowed") return;
    api
      .me()
      .then((r: { nome?: string }) => setDisplayName(r?.nome ?? null))
      .catch(() => setDisplayName(null));
  }, [user, gate]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(!!user && gate === "allowed" && vis.somenteLeitura);
  }, [user, gate, vis.somenteLeitura]);

  // Aba fora das liberadas (inclusive por deep-link #tab=) cai na primeira liberada.
  const verAba = (k: string) => podeVerAba(vis, k);
  useEffect(() => {
    if (!verAba(active)) {
      const primeira = primeiraAbaLiberada(vis, ABAS.map((t) => t.key));
      if (primeira) setActive(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis]);
  // Os cartões do Painel só levam a uma aba liberada (os outros não fazem nada).
  const irPara = (k: string) => {
    if (verAba(k)) setActive(k);
  };

  function login(nome: string) {
    localStorage.setItem(SESSION_KEY, nome);
    setSessionUser(nome);
    setUser(nome);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setSessionUser("");
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;

  if (gate !== "allowed") {
    if (gate === "denied") localStorage.removeItem(SESSION_KEY);
    return <AccessGate status={gate === "checking" ? "checking" : gate} user={user} theme={theme} />;
  }

  const aba = ABAS.find((t) => t.key === active) ?? ABAS[0];
  const aberta = verAba(active) ? active : null;

  return (
    <>
      <AvisoSistema />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>
          <nav className="nav" ref={navRef}>
            {ABAS.filter((t) => verAba(t.key)).map((t) => (
              <button
                key={t.key}
                className={`nav-item ${active === t.key ? "active" : ""}`}
                onClick={() => setActive(t.key)}
              >
                <span className="nav-icon">
                  <t.Icon size={19} />
                </span>
                {t.label}
                {t.key === "agenda" && pendentes > 0 && (
                  <span className="nav-badge" title={pendentes + " pedido(s) de reunião esperando resposta"}>
                    {pendentes}
                  </span>
                )}
                {t.key === "solicitacoes" && novas > 0 && (
                  <span className="nav-badge" title={novas + " solicitação(ões) nova(s) esperando análise"}>
                    {novas}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{aba.label}</h1>
              <p className="muted">{aba.sub}</p>
            </div>
            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">{user.charAt(0).toUpperCase()}</span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          {aviso && verAba(aviso.aba) && (
            <div className="dev-toast" role="status">
              <span>{aviso.texto}</span>
              <button
                className="btn-primary btn-sm"
                onClick={() => {
                  setActive(aviso.aba);
                  if (aviso.aba === "solicitacoes") setChaveSolic((v) => v + 1);
                  setAviso(null);
                }}
              >
                {aviso.botao}
              </button>
              <button className="btn-link" onClick={() => setAviso(null)}>
                depois
              </button>
            </div>
          )}

          {/* Aba não liberada não monta nada (o efeito acima já troca de aba). */}
          <section className="view">
            {aberta === "painel" && (
              <PainelView key={versao} irPara={irPara} onMudou={recarregar} />
            )}
            {aberta === "solicitacoes" && (
              <SolicitacoesView key={chaveSolic} onMudou={recarregar} somenteLeitura={vis.somenteLeitura} />
            )}
            {aberta === "agenda" && (
              <AgendaView versao={versao} onMudou={recarregar} somenteLeitura={vis.somenteLeitura} />
            )}
            {aberta === "lembretes" && <LembretesView onMudou={recarregar} />}
            {aberta === "atualizacoes" && <AtualizacoesView onMudou={recarregar} />}
            {aberta === "auditoria" && <AuditoriaView />}
            {aberta === "monitor" && <MonitorView />}
            {aberta === "backup" && <BackupView />}
          </section>
        </main>
      </div>
    </>
  );
}
